/**
 * Projects: every project the person can see, as cards (components/projectCards.tsx), from one answer,
 * `GET /projects/v1/overview`. At the top the numbers across all of them, as the workbench's Projects view has
 * them; then the cards, what waits on the person first. Archived ones sit behind their own switch. Making a
 * project is a short dialog; the platform gives it its id. An administrator's one setting, archiving idle
 * projects, is behind Settings.
 */

import { formatMoney, type ProjectOverview } from '@aioe/shared';
import { CircleExclamation, Folder, FolderOpen, Gear, ListCheck, Magnifier, Persons, Plus } from '@gravity-ui/icons';
import { Button, Popover, Skeleton } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { routes } from '../api/client.ts';
import { isCloud } from '../edition.ts';
import { MoveProjectDialog, NewProjectDialog, ProjectGrid } from '../components/projectCards.tsx';
import { ChoiceSelect, ErrorNote, Page } from '../components/ui.tsx';
import { ArtEmpty, ArtStat, statNumber, VIEW_TONES, ViewHeader } from '../components/ui/Art.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { SearchBox } from '../components/ui/SearchBox.tsx';
import { useLoad } from '../hooks.ts';
import { listCount } from '../pagedList.tsx';

/** Above this many projects a search earns its place. */
const SEARCH_FROM = 7;
/** Cards drawn at a time; "Show more" draws this many more. */
const PAGE = 24;

export function ProjectsPage() {
  const tone = VIEW_TONES.projects!;
  const navigate = useNavigate();
  const [archived, setArchived] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [drawn, setDrawn] = useState(PAGE);
  const [moving, setMoving] = useState<ProjectOverview['projects'][number]>();
  const load = useLoad(() => routes.projectOverview(archived ? 'archived' : undefined), [archived]);
  const o = load.data;
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!o || !q) return o?.projects ?? [];
    return o.projects.filter((p) => [p.name, p.area?.name, p.purpose, ...p.machines.names].some((x) => x?.toLowerCase().includes(q)));
  }, [o, query]);
  const mayMove = (areaId?: string) => Boolean(o && !isCloud() && (o.can.portfolioManager || (o.can.manages.length > 0 && (!areaId || o.can.manages.includes(areaId)))));

  return (
    <Page>
      <ViewHeader
        tone={tone}
        icon={<FolderOpen />}
        title="Projects"
        blurb={isCloud() ? 'Every project your team works on, who is on it, and what each is doing right now.' : 'Every project in the organisation you can see, who is on it, and what each is doing right now.'}
        actions={
          <>
            {o?.can.admin ? <ProjectSettings /> : null}
            {o?.can.create ? (
              <Button size="sm" variant="primary" onPress={() => setCreating(true)}>
                <Plus className="size-4" />
                New project
              </Button>
            ) : null}
          </>
        }
      />
      <PartBoundary key="projects" name="Projects" tone={tone}>
        <ErrorNote error={load.error} />
        {o ? <Totals overview={o} onNeedsYou={() => navigate('/inbox')} /> : null}

        {o && (o.projects.length > 0 || o.archived > 0 || archived) ? (
          <div className="flex flex-wrap items-center gap-2">
            {(o.archived > 0 || archived) && (
              <Segment aria-label="Which projects" size="sm" selectedKey={archived ? 'archived' : 'live'} onSelectionChange={(k) => setArchived(k === 'archived')} className="w-fit">
                <Segment.Item id="live">In use</Segment.Item>
                <Segment.Item id="archived">Archived{!archived && o.archived ? ` · ${o.archived}` : ''}</Segment.Item>
              </Segment>
            )}
            {o.projects.length >= SEARCH_FROM ? (
              <SearchBox label="Find a project" placeholder="Find a project by name, area or machine" value={query} onChange={(v) => (setQuery(v), setDrawn(PAGE))} className="min-w-0 flex-1 basis-56 sm:max-w-sm" />
            ) : null}
          </div>
        ) : null}

        {!o && load.loading ? <CardsLoading /> : null}
        {o && o.projects.length === 0 ? (
          archived ? (
            <ArtEmpty tone={tone} icon={<Folder />} title="Nothing archived" action={<Button size="sm" variant="secondary" onPress={() => setArchived(false)}>Back to the projects in use</Button>}>
              A project no workbench reports for a long while is archived, out of the way, and its owners can restore it at any time.
            </ArtEmpty>
          ) : (
            <ArtEmpty
              tone={tone}
              icon={<FolderOpen />}
              title="No projects yet"
              action={
                o.can.create ? (
                  <Button size="sm" variant="primary" onPress={() => setCreating(true)}>
                    New project
                  </Button>
                ) : undefined
              }
            >
              A workbench files its project here the first time it opens one while signed in{o.can.create ? ', or you can start one here and people link their repository to it from their workbench' : ''}.
            </ArtEmpty>
          )
        ) : null}
        {o && o.projects.length > 0 && shown.length === 0 ? (
          <ArtEmpty compact tone={tone} icon={<Magnifier />} title="No project matches" action={<Button size="sm" variant="ghost" onPress={() => setQuery('')}>Clear the search</Button>}>
            Nothing you can see is called “{query.trim()}”, sits in an area of that name, or runs on such a machine.
          </ArtEmpty>
        ) : null}
        {o && shown.length > 0 ? <ProjectGrid cards={shown.slice(0, drawn)} overview={o} actions={{ onMove: setMoving, canMove: (c) => mayMove(c.area?.id) }} /> : null}
        {o && shown.length > PAGE ? (
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted">
            <span>{listCount(Math.min(drawn, shown.length), { one: 'project', many: 'projects' }, { total: shown.length, hasMore: shown.length > drawn })}</span>
            {shown.length > drawn ? (
              <Button size="sm" variant="ghost" onPress={() => setDrawn((n) => n + PAGE)}>
                Show more
              </Button>
            ) : null}
          </div>
        ) : null}
      </PartBoundary>
      {o && creating ? <NewProjectDialog isOpen onClose={() => setCreating(false)} overview={o} /> : null}
      {o ? <MoveProjectDialog card={moving} overview={o} onClose={() => setMoving(undefined)} onMoved={load.reload} /> : null}
    </Page>
  );
}

/** The numbers across every project shown: what is working, what waits on the person, and the month's spend. */
function Totals({ overview: o, onNeedsYou }: { overview: ProjectOverview; onNeedsYou: () => void }) {
  const sum = (f: (p: ProjectOverview['projects'][number]) => number) => o.projects.reduce((n, p) => n + f(p), 0);
  const running = sum((p) => p.running);
  const needsYou = sum((p) => p.needsYou);
  const plans = sum((p) => p.plansWaiting);
  const activeWeek = o.projects.filter((p) => p.health.mark === 'working' || p.health.mark === 'online').length;
  const trouble = o.projects.filter((p) => p.health.mark === 'error' || p.health.mark === 'medium').length;
  const spend = sum((p) => p.spendUsd ?? 0);
  const working = o.projects.filter((p) => p.running > 0).length;
  const tasks = sum((p) => p.tasks.open);
  const blocked = sum((p) => p.tasks.blocked);
  return (
    <div className="art-stats">
      <ArtStat tone="blue" motif="rings" icon={<Folder />} label="Projects" value={statNumber(o.projects.length)} detail={trouble ? `${trouble} ${trouble === 1 ? 'needs' : 'need'} a look` : `${activeWeek} active this week`} />
      <ArtStat tone="teal" motif="arc" icon={<Persons />} label="Working now" value={statNumber(running)} detail={running ? `${running === 1 ? 'An agent' : 'Agents'} on ${working} ${working === 1 ? 'project' : 'projects'}` : 'No agent is running'} />
      <ArtStat tone="amber" motif="rings" icon={<CircleExclamation />} label="Waiting on you" value={statNumber(needsYou + plans)} detail={needsYou + plans ? [needsYou ? `${needsYou} in your inbox` : '', plans ? `${plans} ${plans === 1 ? 'plan' : 'plans'} to approve` : ''].filter(Boolean).join(' · ') : 'Nothing waits on you'} needsYou={needsYou + plans > 0} {...(needsYou + plans ? { onPress: onNeedsYou } : {})} />
      <ArtStat tone="green" motif="arc" icon={<ListCheck />} label="Open tasks" value={statNumber(tasks)} detail={[blocked ? `${blocked} blocked` : '', spend > 0 ? `${formatMoney(spend, o.money, { compact: true })} spent in 30 days` : ''].filter(Boolean).join(' · ') || (tasks ? 'None blocked' : 'Nothing on the boards')} />
    </div>
  );
}

function CardsLoading() {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,19rem),1fr))] gap-3" aria-busy="true" aria-label="Loading the projects">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-60 rounded-2xl" />
      ))}
    </div>
  );
}

const AUTO_ARCHIVE = { '0': 'Never', '30': 'After 30 days', '60': 'After 60 days', '90': 'After 90 days', '180': 'After 180 days', '365': 'After a year' } as const;

/** An administrator's setting for every project: whether idle ones are archived on their own. Read only when opened. */
function ProjectSettings() {
  return (
    <Popover>
      <Button size="sm" variant="ghost">
        <Gear className="size-4" />
        Settings
      </Button>
      <Popover.Content placement="bottom end" className="w-80 max-w-[calc(100vw-1rem)]">
        <Popover.Dialog className="grid gap-2 p-3" aria-label="Project settings">
          <AutoArchive />
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

function AutoArchive() {
  const org = useLoad(() => routes.organisationProfile(), []);
  const [error, setError] = useState<string>();
  const days = String(org.data?.projects?.autoArchiveDays ?? 0);
  const options: Record<string, string> = days in AUTO_ARCHIVE ? { ...AUTO_ARCHIVE } : { ...AUTO_ARCHIVE, [days]: `After ${days} days` };
  return (
    <>
      <ChoiceSelect
        label="Archive idle projects"
        className="w-full"
        value={days}
        options={options}
        isDisabled={!org.data}
        onChange={(v) => {
          setError(undefined);
          void routes.setProjectAutoArchiveDays(Number(v)).then(org.reload, (e) => setError((e as Error).message));
        }}
      />
      <p className="m-0 text-xs text-muted">A project no workbench has reported for this long is archived, and its owners are told a week before. An owner restores it at any time.</p>
      <ErrorNote error={error} />
    </>
  );
}
